import { Component, EventEmitter, Injectable, OnInit, Output } from '@angular/core'
import { DepotoCore } from 'depoto-core/DepotoCore'
import { ServiceContainer } from 'depoto-core/src/interfaces'
import {
  Carrier,
  Category,
  Checkout,
  Currency,
  Depot,
  Parameter,
  Payment,
  Producer,
  PurchaseOrderStatus,
  Supplier,
  Tag,
  Vat,
} from 'depoto-core/src/entities'
import { OptionsConfig } from '../configs'
import { SchemaUtil } from '../utils'
import { Institution } from 'depoto-core/src/entities/Institution'
import { TranslateService } from '@ngx-translate/core'
import { CheckDataChanges } from './can-deactivate-guard.service'

type CoreEntityLists = {
  carriers: Carrier[]
  categories: Category[]
  companyCarriers: Carrier[]
  currencies: Currency[]
  checkouts: Checkout[]
  depots: Depot[]
  institutions: Institution[]
  parameters: Parameter[]
  payments: Payment[]
  producers: Producer[]
  purchaseOrderStatuses: PurchaseOrderStatus[]
  suppliers: Supplier[]
  tags: Tag[]
  vats: Vat[]
}

export type AutomaticOrdersSettings = {
  automatizationLevel: 'none' | 'manual-confirmation' | 'automatic'
  frequency: 'weekly' | '2weeks' | 'monthly'
  weekDay: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'
  monthDay: number
  notificationEmail: string
  depots: Array<{ id: number; addressId: number }>
}

@Injectable({
  providedIn: 'root',
})
export class CoreService {
  private core: DepotoCore
  private entityLists: CoreEntityLists = {
    carriers: [],
    categories: [],
    companyCarriers: [],
    currencies: [],
    checkouts: [],
    depots: [],
    institutions: [],
    parameters: [],
    payments: [],
    producers: [],
    purchaseOrderStatuses: [],
    suppliers: [],
    tags: [],
    vats: [],
  }
  private purchaseOrderInProgress: any
  @Output() public onCoreEntityListsLoaded: EventEmitter<CoreEntityLists> = new EventEmitter()
  constructor(private readonly translateService: TranslateService) {
    this.core = new DepotoCore(OptionsConfig.strings.app_prefix, null, null, null)
  }

  get services(): ServiceContainer {
    return this.core.services
  }

  get lists(): CoreEntityLists {
    return this.entityLists
  }

  private setEntityLists(lists: CoreEntityLists): void {
    this.entityLists = lists
    this.onCoreEntityListsLoaded.emit(this.entityLists)
  }

  async loadLists() {
    const startTs = +new Date()
    const results: CoreEntityLists = {
      carriers: [],
      categories: [],
      companyCarriers: [],
      currencies: [],
      checkouts: [],
      depots: [],
      institutions: [],
      parameters: [],
      payments: [],
      producers: [],
      purchaseOrderStatuses: [],
      suppliers: [],
      tags: [],
      vats: [],
    }
    const promises = [
      this.services.carrier.getList({}, SchemaUtil.carrier).then(result => result?.items || []),
      this.services.category
        .getList({ direction: 'ASC', filters: { hasParent: false } }, SchemaUtil.category)
        .then(result => result?.items || []),
      this.services.companyCarrier
        .getList({}, SchemaUtil.companyCarrier)
        .then(result => result?.items?.map(companyCarrier => companyCarrier.carrier) || []),
      this.services.payment
        .getCurrencies(
          {},
          {
            id: null,
            name: null,
            ratio: null,
          },
        )
        .then(result => result?.items || []),
      //if role salesman, fetch just salesman's checkouts
      this.services.checkout
        .getList(
          {
            page: 1,
            sort: 'id',
            direction: 'asc',
          },
          SchemaUtil.checkout,
        )
        .then(result => result?.items || []),
      this.services.depot
        .getList(
          {},
          {
            id: null,
            name: null,
            emailIn: null,
            companyCarriers: {
              id: null,
              name: null,
            },
            checkouts: {
              id: null,
              name: null,
            },
          },
        )
        .then(result => result?.items || []),
      this.services.institution.getList({}, SchemaUtil.basic).then(result => result?.items || []),
      this.services.parameter.getList({}, SchemaUtil.parameter).then(result => result?.items || []),
      this.services.payment.getList({}, SchemaUtil.payment).then(result => result?.items || []),
      this.services.producer.getList({}, SchemaUtil.producer).then(result => result?.items || []),
      this.services.purchase
        .getStatusesList({ sort: 'position', direction: 'ASC' }, SchemaUtil.basic)
        .then(result => result?.items || []),
      this.services.supplier.getList({}, SchemaUtil.supplier).then(result => result?.items || []),
      this.services.tag.getList({}, SchemaUtil.tag).then(result => result?.items || []),
      this.services.vat.getList({}, SchemaUtil.vat).then(result => result?.items || []),
    ]
    ;[
      results.carriers,
      results.categories,
      results.companyCarriers,
      results.currencies,
      results.checkouts,
      results.depots,
      results.institutions,
      results.parameters,
      results.payments,
      results.producers,
      results.purchaseOrderStatuses,
      results.suppliers,
      results.tags,
      results.vats,
    ] = await Promise.all(promises)
    this.setEntityLists(results)
  }

  getDefaultVat(): Vat {
    if (this.lists.vats?.length) {
      return this.lists.vats.filter(v => v.default)[0]
    }
    alert(this.translateService.instant('vat.not-loaded'))
    return null
  }

  getCustomOptions() {
    const company = this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
    const companyCustomOptions =
      typeof company?.customOptions === 'string' ? JSON.parse(company?.customOptions) : company?.customOptions
    return companyCustomOptions || {}
  }

  getAutomaticOrdersSettings(): AutomaticOrdersSettings {
    const defaultSettings: AutomaticOrdersSettings = {
      automatizationLevel: 'none',
      frequency: 'weekly',
      weekDay: 'monday',
      monthDay: 1,
      notificationEmail: '',
      depots: [],
    }
    const company = this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
    const companyCustomOptions = this.getCustomOptions()
    const automaticOrdersSettings = companyCustomOptions?.automaticOrdersSettings || {
      ...defaultSettings,
      notificationEmail: company.email,
    }
    return automaticOrdersSettings
  }

  getPurchaseOrderInProgress() {
    return this.purchaseOrderInProgress
  }

  setPurchaseOrderInProgress(po) {
    this.purchaseOrderInProgress = po
  }
}
